import {
  TIME_REPORT_GROUPS,
  TIME_REPORT_PERIODS,
  type TimeEntryDto,
  type TimeReport,
  type TimeReportGroup,
  type TimeReportPeriod,
  type TimeTotals,
} from '@fos/shared/contracts/time';
import { TIME_REPORT_GROUP_LABELS, TIME_REPORT_PERIOD_LABELS } from '@fos/shared/labels';
import { type CurrencyAmount, sumByCurrency } from '@fos/shared/money';
import { todayLocal } from '@fos/shared/time';
import { keepPreviousData } from '@tanstack/react-query';
import { BarChart3, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { cn } from '../../../lib/cn';
import { formatClock, formatMoneyAuto, plural } from '../../../lib/format';
import { useScope } from '../../../lib/scope';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Field } from '../../../ui/Field';
import { Menu, MenuContent, MenuRadioGroup, MenuRadioItem, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { ClientDot } from '../../clients/components/ClientDot';
import { MobileFilters, TOUCH_TEXT, useIsPhone } from '../../mobile';
import { reportInput, useReportParams } from '../hooks/useReportParams';
import { useTimeOptions } from '../hooks/useTimeOptions';
import {
  amountsText,
  axisLabel,
  axisTicks,
  dayLabel,
  periodNote,
  rangeLongLabel,
  shortDay,
  totalsAmounts,
  weekAxisLabel,
} from '../time-format';

/**
 * Raport czasu (Figma 06 Czas / Raport, 3025:2413): okres (ten tydzień, ten miesiąc, poprzedni miesiąc, zakres),
 * filtry klienta, projektu i osoby (Zespół), kafelki „Łącznie”, „Wartość netto”, „Niezafakturowane”, wykres dzień
 * po dniu, tabela „Według projektu” (klient · projekt, pasek udziału, godziny, kwota, rozwinięcie do wpisów),
 * „Razem”. Kwoty tylko z „Widzi stawki i kwoty” (serwer i tak ich nie wysyła). Sumy liczy serwer z zapisanych kwot.
 */

const ALL = '__wszystkie__';
const NONE = '__brak__';

interface ReportRow {
  key: string;
  label: string;
  sublabel: string | null;
  color: string | null;
  seconds: number;
  /** kwoty per waluta (tylko z „Widzi stawki i kwoty”) */
  amounts?: CurrencyAmount[];
  entries: TimeEntryDto[];
}

function withAmounts(t: TimeTotals): { amounts?: CurrencyAmount[] } {
  const amounts = totalsAmounts(t, 'amount');
  return amounts ? { amounts } : {};
}

function rowsFor(report: TimeReport, group: TimeReportGroup, today: string): ReportRow[] {
  if (group === 'project') {
    return report.clients
      .flatMap((c) =>
        c.projects.map((p) => ({
          key: `${c.clientId ?? ''}|${p.projectId ?? ''}`,
          label: c.name,
          sublabel: p.projectId ? p.name : c.clientId ? null : p.name,
          color: c.color,
          seconds: p.totals.seconds,
          ...withAmounts(p.totals),
          entries: p.entries,
        })),
      )
      .sort((a, b) => b.seconds - a.seconds);
  }
  if (group === 'client') {
    return report.clients.map((c) => ({
      key: c.clientId ?? 'none',
      label: c.name,
      sublabel:
        c.projects.length > 1
          ? `${c.projects.length} ${plural(c.projects.length, ['projekt', 'projekty', 'projektów'])}`
          : null,
      color: c.color,
      seconds: c.totals.seconds,
      ...withAmounts(c.totals),
      entries: c.projects.flatMap((p) => p.entries).sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime()),
    }));
  }
  const all = report.clients.flatMap((c) => c.projects.flatMap((p) => p.entries));
  const map = new Map<string, ReportRow>();
  for (const e of all) {
    const key = group === 'user' ? e.userId : e.date;
    let row = map.get(key);
    if (!row) {
      row = {
        key,
        label: group === 'user' ? (e.user?.name ?? 'Osoba') : dayLabel(e.date, today),
        sublabel: null,
        color: null,
        seconds: 0,
        entries: [],
      };
      map.set(key, row);
    }
    row.seconds += e.durationSeconds;
    row.entries.push(e);
  }
  const rows = [...map.values()];
  for (const r of rows) {
    if (r.entries.every((e) => e.amount !== undefined)) r.amounts = sumByCurrency(r.entries);
    r.entries.sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
  }
  return group === 'day' ? rows.sort((a, b) => b.key.localeCompare(a.key)) : rows.sort((a, b) => b.seconds - a.seconds);
}

/**
 * Kafelek raportu (Figma 06 Raport): etykieta u góry, wartość i krótka notatka w jednej linii — niższy niż Stat
 * z Dziś i profilu klienta. Kolor tylko w kropce przy notatce (np. niezafakturowane).
 */
function ReportTile({
  label,
  value,
  note,
  tone = 'neutral',
  showDot = false,
  className,
}: {
  label: string;
  value: ReactNode;
  note?: string;
  tone?: 'neutral' | 'warning';
  showDot?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5 rounded-xl border px-4 py-3.5', className)}>
      <span className="truncate text-caption font-medium text-fg-muted">{label}</span>
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="shrink-0 text-h4 font-semibold tabular text-fg">{value}</span>
        {note && (
          <span className="flex min-w-0 items-center gap-1.5 text-caption text-fg-muted">
            {showDot && (
              <span
                aria-hidden
                className={cn('size-1.5 shrink-0 rounded-full', tone === 'warning' ? 'bg-warning' : 'bg-fg-muted')}
              />
            )}
            <span className="truncate">{note}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function Chart({ days }: { days: TimeReport['days'] }) {
  const max = Math.max(...days.map((d) => d.seconds), 1);
  const ticks = axisTicks(days.map((d) => d.date));
  const week = days.length <= 7;
  return (
    <figure aria-label="Czas dzień po dniu" className="flex flex-col gap-2">
      <div className="flex h-[120px] items-end gap-[3px] md:gap-1.5">
        {days.map((d) => {
          const h = d.seconds > 0 ? Math.max(4, Math.round((d.seconds / max) * 110)) : 1;
          return (
            <div
              key={d.date}
              title={`${shortDay(d.date)}: ${formatClock(d.seconds)}`}
              className={cn('min-w-0 flex-1 rounded-[2px]', d.seconds > 0 ? 'bg-fg-muted' : 'bg-line')}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>
      <div className="flex gap-[3px] md:gap-1.5" aria-hidden>
        {days.map((d, i) => (
          <span key={d.date} className="relative min-w-0 flex-1 text-caption text-fg-muted">
            {ticks.has(i) && (
              <span
                className={cn(
                  'absolute top-0 whitespace-nowrap',
                  i === 0 ? 'left-0' : i === days.length - 1 ? 'right-0' : 'left-1/2 -translate-x-1/2',
                )}
              >
                {week ? weekAxisLabel(d.date) : axisLabel(d.date, i === 0)}
              </span>
            )}
          </span>
        ))}
      </div>
      <figcaption className="sr-only">
        {days
          .filter((d) => d.seconds > 0)
          .map((d) => `${shortDay(d.date)}: ${formatClock(d.seconds)}`)
          .join(', ')}
      </figcaption>
    </figure>
  );
}

export function TimeReportView({ emptyAction }: { emptyAction?: ReactNode }) {
  const access = useAccess();
  const { scope } = useScope();
  const seesRates = access.flag('seesRates');
  const [params, patch] = useReportParams();
  const today = todayLocal();
  const options = useTimeOptions();
  const report = trpc.time.report.useQuery(reportInput(params, scope), { placeholderData: keepPreviousData });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const phone = useIsPhone();

  const data = report.data;
  const rows = useMemo(() => (data ? rowsFor(data, params.group, today) : []), [data, params.group, today]);
  const maxSeconds = Math.max(...rows.map((r) => r.seconds), 1);

  const clientOptions: ComboboxOption[] = [
    { value: ALL, label: 'Wszyscy klienci' },
    { value: NONE, label: 'Bez klienta', description: 'wewnętrzne' },
    ...(options.data?.clients ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      leading: <ClientDot color={c.color} />,
    })),
  ];
  const projectOptions: ComboboxOption[] = [
    { value: ALL, label: 'Wszystkie projekty' },
    { value: NONE, label: 'Bez projektu' },
    ...(options.data?.projects ?? [])
      .filter((p) => (params.clientId ? p.clientId === params.clientId : params.clientId === null ? !p.clientId : true))
      .map((p) => ({ value: p.id, label: p.name })),
  ];
  const memberOptions: ComboboxOption[] = [
    { value: ALL, label: 'Wszystkie osoby' },
    ...(options.data?.members ?? []).map((m) => ({ value: m.userId, label: m.name })),
  ];
  const toId = (v: string): string | null | undefined => (v === ALL ? undefined : v === NONE ? null : v);
  const fromId = (v: string | null | undefined) => (v === undefined ? ALL : v === null ? NONE : v);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Klient, projekt i osoba: na desktopie w wierszu nad raportem, na telefonie w panelu „Filtry”. */
  const filterCombos = (size: 's' | 'm') => (
    <>
      <Combobox
        aria-label="Klient"
        size={size}
        value={fromId(params.clientId)}
        onValueChange={(v) => patch({ clientId: toId(v), projectId: undefined })}
        options={clientOptions}
        searchPlaceholder="Szukaj klienta…"
        className={size === 's' ? 'md:w-auto md:min-w-[150px]' : undefined}
      />
      <Combobox
        aria-label="Projekt"
        size={size}
        value={fromId(params.projectId)}
        onValueChange={(v) => patch({ projectId: toId(v) })}
        options={projectOptions}
        searchPlaceholder="Szukaj projektu…"
        className={size === 's' ? 'md:w-auto md:min-w-[160px]' : undefined}
      />
      {scope === 'team' && options.data?.canChooseUser && (
        <Combobox
          aria-label="Osoba"
          size={size}
          value={params.userId ?? ALL}
          onValueChange={(v) => patch({ userId: v === ALL ? undefined : v })}
          options={memberOptions}
          searchPlaceholder="Szukaj osoby…"
          className={size === 's' ? 'col-span-2 md:w-auto md:min-w-[150px]' : undefined}
        />
      )}
    </>
  );

  const periodOptions = TIME_REPORT_PERIODS.map((p) => ({ value: p, label: TIME_REPORT_PERIOD_LABELS[p] }));
  const range = data?.range ?? (params.period === 'custom' ? { from: params.from, to: params.to } : null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <div className="hidden md:block">
          <Segmented<TimeReportPeriod>
            aria-label="Okres raportu"
            value={params.period}
            onValueChange={(period) => patch({ period })}
            options={periodOptions}
          />
        </div>
        {/* telefon (lifting 17.10.2026): okres + „Filtry” (klient, projekt, osoba w dolnym panelu) */}
        <div className="flex items-center gap-2 md:hidden">
          <div className="min-w-0 flex-1">
            <Select<TimeReportPeriod>
              aria-label="Okres raportu"
              value={params.period}
              onValueChange={(period) => patch({ period })}
              options={periodOptions}
              className="max-md:h-11"
            />
          </div>
          {phone && (
            <MobileFilters
              count={[params.clientId, params.projectId, params.userId].filter((v) => v !== undefined).length}
              onClear={() => patch({ clientId: undefined, projectId: undefined, userId: undefined })}
              showLabel="Pokaż raport"
            >
              {filterCombos('m')}
            </MobileFilters>
          )}
        </div>
        {params.period === 'custom' ? (
          <div className="flex items-end gap-2">
            <Field label="Od" className="w-[160px]">
              <DateInput size="s" value={params.from} onValueChange={(from) => from && patch({ from })} />
            </Field>
            <Field label="Do" className="w-[160px]">
              <DateInput size="s" value={params.to} onValueChange={(to) => to && patch({ to })} />
            </Field>
          </div>
        ) : (
          range && (
            <span className="inline-flex items-center gap-2 px-1 text-body-s text-fg">
              <Calendar size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
              {rangeLongLabel(range)}
            </span>
          )
        )}
        <span className="hidden flex-1 md:block" />
        {!phone && <div className="grid grid-cols-2 gap-2 md:flex">{filterCombos('s')}</div>}
      </div>

      {report.isPending ? (
        <div role="status" aria-label="Wczytywanie raportu" className="flex flex-col gap-6">
          <div className="grid gap-3 md:grid-cols-3">
            <Skeleton className="h-[84px] rounded-xl" />
            <Skeleton className="h-[84px] rounded-xl" />
            <Skeleton className="h-[84px] rounded-xl" />
          </div>
          <Section>
            <ListSkeleton rows={3} />
          </Section>
        </div>
      ) : report.isError && !data ? (
        <Section>
          <ErrorState
            onRetry={() => void report.refetch()}
            retrying={report.isFetching}
            description="Nie udało się wczytać raportu."
          />
        </Section>
      ) : data ? (
        <ReportBody
          report={data}
          rows={rows}
          maxSeconds={maxSeconds}
          seesRates={seesRates}
          expanded={expanded}
          onToggle={toggle}
          group={params.group}
          onGroup={(group) => patch({ group })}
          emptyAction={emptyAction}
          stale={report.isFetching}
        />
      ) : null}
    </div>
  );
}

function ReportBody({
  report,
  rows,
  maxSeconds,
  seesRates,
  expanded,
  onToggle,
  group,
  onGroup,
  emptyAction,
  stale,
}: {
  report: TimeReport;
  rows: ReportRow[];
  maxSeconds: number;
  seesRates: boolean;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  group: TimeReportGroup;
  onGroup: (g: TimeReportGroup) => void;
  emptyAction?: ReactNode;
  stale: boolean;
}) {
  const t = report.totals;
  const showMoney = seesRates && t.amount !== undefined;
  const top = report.topUnbilled;
  const amounts = totalsAmounts(t, 'amount');
  const unbilledAmounts = totalsAmounts(t, 'unbilledAmount');
  // kwota kafelka to suma wszystkich klientów, więc przy kilku klientach notatka mówi o wszystkich (jak Rozliczenia)
  const unbilledNote =
    report.unbilledClientCount > 1
      ? `${formatClock(t.unbilledSeconds)} · ${report.unbilledClientCount} ${plural(report.unbilledClientCount, ['klient', 'klientów', 'klientów'])}`
      : top
        ? `${top.name} · ${formatClock(top.seconds)}`
        : 'wszystko rozliczone';
  // kafelki jak Stat: białe karty na szarym tle aplikacji (DECISIONS 2026-10-07 „Nowy układ wizualny”)
  const tileClass = 'border-line bg-panel';
  // kwoty partnera (DECISIONS 2026-10-08 [P]): raport jednej osoby = „Do wypłaty partnerowi” (dokument do rachunku dla
  // prowadzącego), kilka osób (owner / admin / manager) = „Koszt partnerów”
  const partner = t.partner;
  const partnerPeople = new Set(
    report.clients.flatMap((c) =>
      c.projects.flatMap((p) => p.entries.filter((e) => e.partnerRate != null).map((e) => e.userId)),
    ),
  );
  const partnerTile = partner ? (
    partnerPeople.size > 1 ? (
      <ReportTile
        className={tileClass}
        label="Koszt partnerów"
        value={formatMoneyAuto(partner.amount, partner.currency)}
        note={`${formatClock(partner.seconds)} · do wypłaty ${formatMoneyAuto(partner.unsettledAmount, partner.currency)}`}
        showDot={false}
      />
    ) : (
      <ReportTile
        className={tileClass}
        label="Do wypłaty partnerowi"
        value={formatMoneyAuto(partner.unsettledAmount, partner.currency)}
        note={
          partner.unsettledAmount === partner.amount
            ? `${formatClock(partner.seconds)} ze stawką partnera`
            : `razem ${formatMoneyAuto(partner.amount, partner.currency)}, część rozliczona`
        }
        showDot={false}
      />
    )
  ) : null;
  return (
    <div className={cn('flex flex-col gap-6 transition-opacity', stale && 'opacity-70')} aria-busy={stale || undefined}>
      <div className={cn('grid gap-3', showMoney && partnerTile ? 'md:grid-cols-4' : 'md:grid-cols-3')}>
        <ReportTile
          className={tileClass}
          label="Łącznie"
          value={<Mono>{formatClock(t.seconds)}</Mono>}
          note={`${periodNote(report.period, report.range)}${t.nonBillableSeconds > 0 && t.billableSeconds > 0 ? ` · ${formatClock(t.nonBillableSeconds)} nierozliczane` : ''}`}
          showDot={false}
        />
        {showMoney ? (
          <ReportTile
            className={tileClass}
            label="Wartość netto"
            value={amountsText(amounts ?? [])}
            note={`${t.projectCount} ${plural(t.projectCount, ['projekt', 'projekty', 'projektów'])}`}
            showDot={false}
          />
        ) : partnerTile ? (
          partnerTile
        ) : (
          <ReportTile
            className={tileClass}
            label="Rozliczany"
            value={<Mono>{formatClock(t.billableSeconds)}</Mono>}
            note={`nierozliczany ${formatClock(t.nonBillableSeconds)}`}
            showDot={false}
          />
        )}
        <ReportTile
          className={tileClass}
          label="Niezafakturowane"
          value={
            showMoney && unbilledAmounts ? amountsText(unbilledAmounts) : <Mono>{formatClock(t.unbilledSeconds)}</Mono>
          }
          note={unbilledNote}
          tone={t.unbilledSeconds > 0 ? 'warning' : 'neutral'}
          showDot={t.unbilledSeconds > 0}
        />
        {showMoney && partnerTile}
      </div>

      {report.days.length > 0 && t.seconds > 0 && (
        <Section>
          <Chart days={report.days} />
        </Section>
      )}

      {rows.length === 0 ? (
        <Section>
          <EmptyState
            icon={BarChart3}
            title="Brak czasu w tym okresie"
            description="Zmień okres albo filtry, żeby zobaczyć wpisy."
            action={emptyAction}
          />
        </Section>
      ) : (
        <Section aria-label="Tabela raportu">
          <div className="flex h-10 items-center gap-4 border-b border-line">
            <Menu>
              <MenuTrigger asChild>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 rounded-md text-body-s font-semibold text-fg hover:text-fg-secondary focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
                >
                  {TIME_REPORT_GROUP_LABELS[group]}
                  <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
                </button>
              </MenuTrigger>
              <MenuContent>
                <MenuRadioGroup value={group} onValueChange={(v) => onGroup(v as TimeReportGroup)}>
                  {TIME_REPORT_GROUPS.map((g) => (
                    <MenuRadioItem key={g} value={g}>
                      {TIME_REPORT_GROUP_LABELS[g]}
                    </MenuRadioItem>
                  ))}
                </MenuRadioGroup>
              </MenuContent>
            </Menu>
            <span className="flex-1" />
            <span className="w-16 text-right text-caption text-fg-muted">Godziny</span>
            {showMoney && (
              <span className="hidden w-24 text-right text-caption text-fg-muted sm:inline">Kwota netto</span>
            )}
            <span className="w-4" aria-hidden />
          </div>
          <ul className="flex flex-col">
            {rows.map((row) => {
              const open = expanded.has(row.key);
              return (
                <li key={row.key} className="border-b border-line">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => onToggle(row.key)}
                    className="flex min-h-[52px] w-full items-center gap-4 py-2 text-left transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2 md:w-[300px] md:flex-none">
                      {(group === 'project' || group === 'client') && <ClientDot color={row.color} />}
                      <span className="truncate text-body-s font-medium text-fg">{row.label}</span>
                      {row.sublabel && (
                        <span className="min-w-0 truncate text-body-s text-fg-muted">{row.sublabel}</span>
                      )}
                    </span>
                    <span
                      className="hidden h-1 min-w-0 flex-1 overflow-hidden rounded-[2px] bg-elevated md:block"
                      aria-hidden
                    >
                      <span
                        className={cn('block h-full rounded-[2px]', !row.color && 'bg-fg-muted')}
                        style={{
                          width: `${Math.max(1, (row.seconds / maxSeconds) * 100)}%`,
                          ...(row.color ? { backgroundColor: row.color } : {}),
                        }}
                      />
                    </span>
                    <Mono className="w-16 text-right text-body-s text-fg">{formatClock(row.seconds)}</Mono>
                    {showMoney && (
                      <Mono className="hidden w-24 text-right text-body-s text-fg sm:inline">
                        {row.amounts ? amountsText(row.amounts) : '—'}
                      </Mono>
                    )}
                    <ChevronRight
                      size={16}
                      strokeWidth={1.75}
                      aria-hidden
                      className={cn('w-4 shrink-0 text-fg-muted transition-transform', open && 'rotate-90')}
                    />
                  </button>
                  {open && (
                    <ul className="mb-2 flex flex-col rounded-lg bg-surface px-3 py-1">
                      {row.entries.map((e) => (
                        <li
                          key={e.id}
                          className="flex min-h-10 flex-wrap items-center gap-x-4 gap-y-0.5 py-1.5 text-body-s"
                        >
                          <Mono className="w-16 shrink-0 text-caption text-fg-muted">{shortDay(e.date)}</Mono>
                          <span className="min-w-0 flex-1 truncate text-fg">
                            {e.description || 'Bez opisu'}
                            {group !== 'project' && (e.client || e.project) && (
                              <span className="text-fg-muted">
                                {' · '}
                                {[e.client?.name, e.project?.name].filter(Boolean).join(' · ')}
                              </span>
                            )}
                            {group !== 'user' && report.scope === 'team' && e.user && (
                              <span className="text-fg-muted"> · {e.user.name}</span>
                            )}
                          </span>
                          <Mono className="hidden text-caption text-fg-secondary sm:inline">
                            {e.start} – {e.end}
                          </Mono>
                          <Mono className="w-12 text-right text-caption text-fg">{formatClock(e.durationSeconds)}</Mono>
                          {showMoney && (
                            <Mono className="hidden w-24 text-right text-caption text-fg-secondary sm:inline">
                              {e.billable && e.amount !== undefined ? formatMoneyAuto(e.amount, e.currency) : '—'}
                            </Mono>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex min-h-[52px] items-center gap-4">
            <span className="flex-1 text-body-s font-semibold text-fg">Razem</span>
            <Mono className="w-16 text-right text-body-s font-semibold text-fg">{formatClock(t.seconds)}</Mono>
            {showMoney && (
              <Mono className="hidden w-24 text-right text-body-s font-semibold text-fg sm:inline">
                {amountsText(amounts ?? [])}
              </Mono>
            )}
            <span className="w-4" aria-hidden />
          </div>
          {report.truncated && (
            <p className="text-caption text-fg-muted">Raport obejmuje najnowsze 10 000 wpisów. Zawęź okres.</p>
          )}
        </Section>
      )}
      <span className="sr-only" aria-live="polite">
        {stale ? 'Odświeżanie raportu' : ''}
      </span>
    </div>
  );
}
